import { Trans } from "@lingui/macro";
import dayjs from "dayjs";

import type {
  Passation,
  Questionnaire,
  QuestionnaireItem,
} from "@app/utils/questionnaires";
import { rawSum } from "@app/utils/questionnaires";

interface QuestionnaireMatrixProps {
  questionnaire: Questionnaire;
  /** Le champ de score depuis lequel la matrice a été ouverte — sa ligne de total est mise en avant. */
  fieldKey?: string;
  /**
   * Édition granulaire. Absent = lecture seule.
   *
   * Reçoit la passation, la question et la nouvelle réponse ; c'est l'appelant qui recalcule les
   * totaux, parce que lui seul sait s'il édite une correction en cours ou regarde l'agrégat.
   */
  onAnswerChange?: (
    passationIndex: number,
    itemKey: string,
    value: number | null,
  ) => void;
  /** Réponses en cours d'édition, par index de passation. Prioritaires sur celles de la source. */
  draft?: Record<number, Record<string, number>>;
  /**
   * Réponses corrigées par un clinicien, indexées par date (AAAA-MM-JJ).
   *
   * Elles priment sur celles de la source et sont signalées comme telles. Sans ce relais, la
   * matrice se re-dérive du document à chaque ouverture et réaffiche la réponse que le médecin
   * vient de contredire, sous le total corrigé.
   */
  corrected?: Record<string, Record<string, number>>;
}

/** Fond de cellule proportionnel à la sévérité — 0 pâle, maximum saturé. */
function cellTone(value: number | undefined, max: number): string {
  if (value === undefined || !Number.isFinite(value)) return "bg-white text-slate-300";
  const ratio = max > 0 ? value / max : 0;
  if (ratio === 0) return "bg-slate-50 text-slate-400";
  if (ratio <= 0.34) return "bg-blue-50 text-blue-900";
  if (ratio <= 0.67) return "bg-blue-100 text-blue-900";
  return "bg-primary text-white";
}

function answersOf(
  passation: Passation,
  index: number,
  draft?: Record<number, Record<string, number>>,
  corrected?: Record<string, Record<string, number>>,
): Record<string, number> {
  const validated = passation.date
    ? corrected?.[String(passation.date).slice(0, 10)]
    : undefined;
  return draft?.[index] ?? validated ?? passation.answers;
}

/** Cette réponse-là a-t-elle été posée par un clinicien plutôt que lue dans le document ? */
function isCorrected(
  passation: Passation,
  itemKey: string,
  corrected?: Record<string, Record<string, number>>,
): boolean {
  if (!passation.date) return false;
  const validated = corrected?.[String(passation.date).slice(0, 10)];
  if (!validated || !(itemKey in validated)) return false;
  return validated[itemKey] !== passation.answers[itemKey];
}

/**
 * Les réponses d'un questionnaire, une ligne par question, une colonne par passation.
 *
 * C'est la représentation qui rend une dérive visible : une question qui se dégrade de passation en
 * passation se lit sur sa ligne, et une colonne entière cochée au maximum — le cas qui a motivé
 * cet écran — se voit sans la chercher, là où la courbe du total n'en montre qu'un pic.
 *
 * Deux totaux sont affichés sous la matrice et jamais fusionnés : celui que les réponses impliquent
 * et celui que la source a écrit. Les voir diverger EST l'information ; n'en garder qu'un rendrait
 * une erreur de transcription indistinguable d'une lecture correcte.
 */
export default function QuestionnaireMatrix({
  questionnaire,
  fieldKey,
  onAnswerChange,
  draft,
  corrected,
}: QuestionnaireMatrixProps) {
  const { items, passations } = questionnaire;
  const editable = typeof onAnswerChange === "function";

  if (passations.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-slate-500 italic">
        <Trans>
          Aucune passation documentée pour ce patient : les scores connus proviennent d'un
          courrier, pas d'un questionnaire rempli.
        </Trans>
      </div>
    );
  }

  // Plus récente à gauche : c'est la passation qu'on regarde en premier.
  const columns = passations.map((p, i) => ({ passation: p, index: i })).reverse();

  const renderCell = (item: QuestionnaireItem, column: (typeof columns)[number]) => {
    // Une question qui ne compte pas dans le total porte une réponse que la cotation ne sait pas
    // lire — une date. Elle s'affiche telle que la source l'écrit, et reste en lecture seule même
    // dans l'éditeur : rien dans le total n'en dépend, donc rien ne saurait quoi faire d'une
    // correction.
    if (item.scored === false) {
      const text = column.passation.notes?.[item.key];
      return (
        <td key={column.index} className="p-1 text-center">
          <span className="inline-block min-w-[30px] px-2 py-1 text-sm text-slate-600">
            {text || "–"}
          </span>
        </td>
      );
    }

    const answers = answersOf(column.passation, column.index, draft, corrected);
    const value = answers[item.key];
    // Sans objet plutôt que manquante : le formulaire lui-même laisse cette question à « NR »
    // quand elle ne s'applique pas, et le total est pris sur celles qui s'appliquaient.
    const notApplicable = value === undefined && item.optional === true;
    // Vert = posé par un clinicien, comme partout ailleurs sur la page. L'anneau plutôt que le
    // fond : l'intensité du fond porte déjà la sévérité de la réponse, et la repeindre ferait
    // perdre la lecture qui justifie cette vue.
    const humanRing = isCorrected(column.passation, item.key, corrected)
      ? " ring-2 ring-green-500"
      : "";

    if (!editable) {
      return (
        <td key={column.index} className="p-1 text-center">
          <span
            title={
              humanRing
                ? "Réponse corrigée par un clinicien"
                : notApplicable
                  ? "Sans objet pour ce patient — le total est pris sur les questions applicables"
                  : undefined
            }
            className={`inline-block min-w-[30px] rounded px-2 py-1 text-sm font-medium tabular-nums ${cellTone(
              value,
              item.max ?? 0,
            )}${humanRing}`}
          >
            {value === undefined ? (notApplicable ? "n/a" : "–") : value}
          </span>
        </td>
      );
    }

    return (
      <td key={column.index} className="p-1 text-center">
        <input
          type="number"
          min={0}
          max={item.max}
          step={1}
          value={value === undefined ? "" : value}
          placeholder={notApplicable ? "n/a" : undefined}
          aria-label={`${item.wording} — ${dayjs(column.passation.date).format("DD/MM/YYYY")}`}
          onChange={(e) => {
            const raw = e.target.value.trim();
            onAnswerChange!(
              column.index,
              item.key,
              raw === "" ? null : Number(raw),
            );
          }}
          className={`w-16 rounded border border-slate-200 px-2 py-1 text-center text-sm tabular-nums
            focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary ${cellTone(
              value,
              item.max ?? 0,
            )}${humanRing}`}
        />
      </td>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-600">
              <Trans>Question</Trans>
            </th>
            {columns.map((column) => {
              const missing = column.passation.missing ?? [];
              return (
                <th
                  key={column.index}
                  className="whitespace-nowrap bg-slate-50 px-2 py-2 text-center font-semibold text-slate-600 tabular-nums"
                  title={
                    missing.length > 0
                      ? `${column.passation.form} — formulaire incomplet : ${missing.length} question(s) sans réponse. Le total affiché est celui écrit par la source.`
                      : column.passation.form
                  }
                >
                  {dayjs(column.passation.date).format("DD/MM/YY")}
                  {missing.length > 0 && (
                    <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      <Trans>incomplet</Trans>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.key} className="border-b border-slate-100">
              <td className="sticky left-0 bg-white px-3 py-1.5 text-slate-700">
                {item.wording}
                {item.scored === false ? (
                  <span className="ml-1 text-xs italic text-slate-400">
                    <Trans>ne compte pas dans le score</Trans>
                  </span>
                ) : (
                  <span className="ml-1 text-xs text-slate-400">/{item.max}</span>
                )}
              </td>
              {columns.map((column) => renderCell(item, column))}
            </tr>
          ))}

          {/* Somme brute — l'étape intermédiaire que le formulaire imprime lui-même
              (« 11/50 soit 22 % ») et sans laquelle le pourcentage tombe du ciel. */}
          <tr className="border-t-2 border-slate-200 bg-slate-50">
            <td className="sticky left-0 bg-slate-50 px-3 py-2 font-medium text-slate-600">
              <Trans>Somme des réponses</Trans>
            </td>
            {columns.map((column) => {
              const { sum, max, complete } = rawSum(
                items,
                answersOf(column.passation, column.index, draft, corrected),
              );
              return (
                <td
                  key={column.index}
                  className="px-2 py-2 text-center font-medium tabular-nums text-slate-700"
                >
                  {complete ? `${sum}/${max}` : "·"}
                </td>
              );
            })}
          </tr>

          {questionnaire.scores.map((score) => (
            <tr
              key={score.field_key}
              className={
                score.field_key === fieldKey
                  ? "bg-blue-50 font-semibold text-blue-900"
                  : "text-slate-700"
              }
            >
              <td className="sticky left-0 bg-inherit px-3 py-2">
                {score.label}
                {score.how ? (
                  <span className="ml-1 text-xs font-normal text-slate-400">
                    {score.how}
                  </span>
                ) : null}
              </td>
              {columns.map((column) => {
                const computed = column.passation.scores.find(
                  (s) => s.field_key === score.field_key,
                );
                const source = computed?.source ?? null;
                const comparable = computed?.source_scaled ?? source;
                const value = computed?.computed ?? null;
                // Le désaccord se juge sur l'échelle du champ ; le nombre montré reste celui que
                // le médecin lit sur le document.
                const diverges =
                  value !== null &&
                  comparable !== null &&
                  comparable !== undefined &&
                  Math.abs(value - comparable) > 0.005;
                // Hors de l'échelle que les médecins déclarent. Montré, pas écarté : c'est une
                // question à leur poser, et une valeur supprimée ne se pose pas.
                const declared = computed?.declared_range;
                const outOfDeclared =
                  value !== null &&
                  Array.isArray(declared) &&
                  (value < declared[0] || value > declared[1]);
                return (
                  <td
                    key={column.index}
                    className="px-2 py-2 text-center tabular-nums"
                  >
                    {value === null ? (
                      source === null ? (
                        "–"
                      ) : (
                        // Non calculable : il manque une réponse. Le nombre montré est celui que la
                        // source a écrit, signalé comme tel — l'effacer perdrait la seule mesure
                        // que ce jour-là possède.
                        <span
                          className="font-normal text-slate-500"
                          title="Formulaire incomplet : total écrit par la source, non recalculable"
                        >
                          ({Number(source.toFixed(2))})
                        </span>
                      )
                    ) : (
                      Number(value.toFixed(2))
                    )}
                    {outOfDeclared && (
                      <span
                        className="ml-0.5 cursor-help text-xs font-normal text-primary"
                        title={`Hors de l'échelle déclarée (${declared![0]} à ${declared![1]}) — à expliquer`}
                      >
                        ?
                      </span>
                    )}
                    {/* La source dit autre chose. Signalé plutôt que masqué : c'est
                        précisément ce qu'une campagne de correction cherche. */}
                    {diverges && (
                      <span
                        className="ml-1 text-xs font-normal text-red-600"
                        title={
                          comparable !== null && comparable !== undefined && comparable !== source
                            ? `Valeur écrite par la source : ${source} (soit ${comparable} sur l'échelle du champ)`
                            : "Valeur écrite par la source"
                        }
                      >
                        ≠ {Number(source!.toFixed(2))}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
